import React, { FC } from 'react'
import {
  Box,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import ToggleOnIcon from '@mui/icons-material/ToggleOn'
import ToggleOffIcon from '@mui/icons-material/ToggleOff'

import {
  DataTable,
  type DataTableColumn,
  EmptyState,
} from '@/apps/latest/components/portal/notion'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import {
  FormatInsertRule,
  soapSectionLabels,
} from '@/features/internal/format-insert-rules/types/formatInsertRule'

interface Props {
  rules: FormatInsertRule[] | undefined
  loading: boolean
  onEdit: (rule: FormatInsertRule) => void
  onDelete: (id: string) => void
  onToggleActive: (id: string) => void
}

const COLUMNS: DataTableColumn[] = [
  { key: 'name', label: 'ルール名' },
  { key: 'section', label: '対象', align: 'center' },
  { key: 'keywords', label: 'キーワード' },
  { key: 'insert', label: '挿入テキスト' },
  { key: 'state', label: '状態', align: 'center' },
  { key: 'actions', label: '', align: 'right' },
]

export const FormatInsertRuleTable: FC<Props> = ({
  rules,
  loading,
  onEdit,
  onDelete,
  onToggleActive,
}) => {
  if (loading) {
    return (
      <Stack gap={1}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rectangular" height={48} />
        ))}
      </Stack>
    )
  }

  if (!rules || rules.length === 0) {
    return (
      <EmptyState
        title="まだルールが作成されていません"
        hint="「新規ルール」 ボタンから作成してください"
      />
    )
  }

  return (
    <DataTable columns={COLUMNS}>
      {rules.map((rule) => (
        <TableRow key={rule.id} hover>
          <TableCell>
            <Stack gap={0.25}>
              <Typography sx={PORTAL_TEXT.cellBody}>{rule.name}</Typography>
              {rule.description && (
                <Typography sx={PORTAL_TEXT.cellMeta}>
                  {rule.description}
                </Typography>
              )}
            </Stack>
          </TableCell>
          <TableCell align="center">
            <Typography sx={PORTAL_TEXT.cellSecondary}>
              {soapSectionLabels[rule.target_section]}
            </Typography>
          </TableCell>
          <TableCell>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {rule.keywords?.map((keyword, index) => (
                <Chip
                  key={index}
                  label={keyword}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 22,
                    fontSize: 12,
                    color: colorTokens.fg.muted,
                    borderColor: colorTokens.border.subtle,
                    bgcolor: 'transparent',
                  }}
                />
              ))}
            </Box>
          </TableCell>
          <TableCell>
            <Typography
              sx={{
                ...PORTAL_TEXT.cellSecondary,
                maxWidth: 320,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {rule.insert_text}
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Typography
              sx={{
                ...PORTAL_TEXT.cellMeta,
                color: rule.is_active
                  ? colorTokens.fg.default
                  : colorTokens.fg.muted,
              }}
            >
              {rule.is_active ? '有効' : '無効'}
            </Typography>
          </TableCell>
          <TableCell align="right">
            <Stack direction="row" gap={0.5} justifyContent="flex-end">
              <Tooltip title={rule.is_active ? '無効化' : '有効化'} arrow>
                <IconButton
                  size="small"
                  onClick={() => rule.id && onToggleActive(rule.id)}
                  sx={{
                    color: rule.is_active
                      ? colorTokens.fg.default
                      : colorTokens.fg.muted,
                  }}
                >
                  {rule.is_active ? (
                    <ToggleOnIcon fontSize="small" />
                  ) : (
                    <ToggleOffIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="編集" arrow>
                <IconButton size="small" onClick={() => onEdit(rule)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="削除" arrow>
                <IconButton
                  size="small"
                  onClick={() => rule.id && onDelete(rule.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </TableCell>
        </TableRow>
      ))}
    </DataTable>
  )
}
